import React from 'react';
import { isStatsigConfigured, StatsigAdapter } from '@/providers/adapters';
import { FeatureFlagContext } from '@/providers/feature-flag-context';
import type { FeatureFlagContextValue } from '@/types/context/feature-flag-context.types';
import { getTenantSlugFromHostname } from '@/utils/path-utils';

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/** Default value when feature flags are disabled */
const DISABLED_VALUE: FeatureFlagContextValue = {
  isFeatureEnabled: () => false,
};

/**
 * Feature flag provider with full abstraction.
 *
 * This provider is stable - to switch feature flag libraries,
 * only change the adapter in `@/providers/adapters/`.
 *
 * Web-login has no authenticated user; tenantSlug is derived from the
 * hostname (web-login runs at <tenant>.<domain>).
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const tenantSlug = getTenantSlugFromHostname();

  // No adapter configured = disabled mode (all flags false)
  if (!isStatsigConfigured()) {
    return (
      <FeatureFlagContext.Provider value={DISABLED_VALUE}>
        {children}
      </FeatureFlagContext.Provider>
    );
  }

  return (
    <StatsigAdapter tenantSlug={tenantSlug}>
      {value => (
        <FeatureFlagContext.Provider value={value}>
          {children}
        </FeatureFlagContext.Provider>
      )}
    </StatsigAdapter>
  );
}
